import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  CircularProgress,
  Chip,
  Tooltip,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Divider,
} from '@mui/material';
import { CacheKey } from '../../../types/cache-keys';
import ResourceRelationPanel from '../Panels/ResourceRelationPanel';
import TagManagementSection from '../Common/TagManagementSection';
import SnackableCourseButton from '../SnackableCourse/SnackableCourseButton';
import SnackableCourseManager from '../SnackableCourse/SnackableCourseManager';
import { asArray } from '../../../support/utils';
import {
  useAddGtmSolutionUseCaseComponentMutation,
  useAttachedItemsUpdateMutation,
  useFetchSingleGtmSolutionUseCase,
  useFetchSingleGtmSolutionUseCaseComponent,
  useUpdateGtmSolutionUseCaseComponentMutation,
} from '../../../hooks/gtm-solution-hooks';
import { GtmProductFeature, GtmSolutionUseCaseComponent } from '../../../types/go-to-market';
import { Product } from '../../../types/product';
import { useFetchCiscoProducts, useFetchProductFeatures } from '../../../hooks/product-hooks';
import AdminConfirmDialog from '../Common/AdminConfirmDialog';

type Props = {
  open: boolean;
  mode: 'create' | 'edit';
  gtmId: number;
  useCaseId: number;
  componentId?: number;
  onClose: () => void;
  onSaved: () => void;
  readOnly?: boolean;
};

export default function GtmSolutionUseCaseComponentEditorDialog({
                                                                  open,
                                                                  mode,
                                                                  gtmId,
                                                                  useCaseId,
                                                                  componentId,
                                                                  onClose,
                                                                  onSaved,
                                                                  readOnly = false,
                                                                }: Props) {
  const isEdit = mode === 'edit' && !!componentId;
  const [hasHydrated, setHasHydrated] = useState(false);
  const [courseManagerOpen, setCourseManagerOpen] = useState(false);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  const { data: ciscoProducts, isLoading: productsLoading } = useFetchCiscoProducts();
  const ciscoProductById = useMemo(() => {
    if (!ciscoProducts) return new Map<number, Product>();
    const m = new Map<Product['id'], Product>();
    for (const p of ciscoProducts) m.set(p.id, p);
    return m;
  }, [ciscoProducts]);

  const { data: useCase, isLoading: useCaseLoading } = useFetchSingleGtmSolutionUseCase(gtmId, useCaseId);
  const { data: componentData, isLoading: componentLoading } =
    useFetchSingleGtmSolutionUseCaseComponent(useCaseId, componentId);

  const component = useMemo(() => {
    if (isEdit) {
      if (useCaseLoading && componentLoading) return null;
      if (componentData) return componentData;
      const found = asArray(useCase?.components).find((s) => s.id === componentId);
      return found ?? null;
    }
    return null;
  }, [isEdit, componentId, useCase, useCaseLoading, componentData, componentLoading]);

  const { mutateAsync: addComponent, isPending: addingComponent } =
    useAddGtmSolutionUseCaseComponentMutation(useCaseId, gtmId);
  const { mutateAsync: updateComponent, isPending: updatingComponent } =
    useUpdateGtmSolutionUseCaseComponentMutation(useCaseId, gtmId);
  const { mutateAsync: updateJoins, isPending: updatingJoins } =
    useAttachedItemsUpdateMutation<GtmProductFeature, GtmSolutionUseCaseComponent>({
      type: CacheKey.GtmSolutionUseCaseComponentFeature,
      architectureId: gtmId,
      useCaseId: useCaseId,
    });

  const [form, setForm] = useState<any>({
    name: '',
    short_description: "",
    description: '',
    order: 0,
    use_case: useCaseId,
  });
  const [initialForm, setInitialForm] = useState<any>({
    name: '',
    short_description: "",
    description: '',
    order: 0,
    use_case: useCaseId,
  });

  const [selectedProducts, setSelectedProducts] = useState<Record<Product['id'], GtmProductFeature['id'][]>>({});
  const [initialSelectedProducts, setInitialSelectedProducts] = useState<Record<Product['id'], GtmProductFeature['id'][]>>({});

  const selectedProductChips = useMemo(() => {
    return Object.entries(selectedProducts)
      .filter(([k, arr]) => (arr || []).length > 0 && ciscoProductById.has(Number(k)))
      .map(([k]) => ciscoProductById.get(Number(k)));
  }, [selectedProducts, ciscoProductById]);

  const [activeProductId, setActiveProductId] = useState<number | null>(null);

  const { data: features, isLoading: featuresLoading } = useFetchProductFeatures(activeProductId);
  const featuresById = useMemo(() => {
    const map = new Map<GtmProductFeature['id'], GtmProductFeature>();
    for (const feature of features ?? []) map.set(feature.id, feature);
    return map;
  }, [features]);

  const activeSelectedFeatures = useMemo(() => {
    if (!activeProductId) return [];
    return (selectedProducts[activeProductId] ?? []).map((id) => featuresById.get(id)).filter(Boolean);
  }, [activeProductId, selectedProducts, featuresById]);

  useEffect(() => {
    if (!open) {
      setHasHydrated(false);
      return;
    }
  }, [open]);

  useEffect(() => {
    setHasHydrated(false);
  }, [mode, gtmId, useCaseId, componentId]);

  useEffect(() => {
    if (!open || hasHydrated || mode !== 'create') return;

    const emptyForm = {
      name: '',
      short_description: "",
      description: '',
      order: 0,
      use_case: useCaseId,
    };

    setForm(emptyForm);
    setInitialForm(emptyForm);
    setSelectedProducts({});
    setInitialSelectedProducts({});
    setActiveProductId(null);
    setHasHydrated(true);
  }, [open, hasHydrated, mode, useCaseId]);

  useEffect(() => {
    if (!open || hasHydrated || !isEdit || !component) return;

    const nextForm = {
      ...component,
      name: component.name ?? '',
      short_description: component.short_description ?? "",
      description: component.description ?? '',
      order: component.order ?? 0,
      use_case: useCaseId,
    };

    const featureByProduct: Record<Product['id'], Set<GtmProductFeature['id']>> = {};

    for (const feature of component?.product_feature ?? []) {
      const productId = typeof feature?.product === 'number' ? feature.product : feature?.product?.id ?? null;
      if (!productId) continue;

      if (!featureByProduct[productId]) featureByProduct[productId] = new Set();
      featureByProduct[productId].add(feature.id);
    }

    const products: Record<Product['id'], GtmProductFeature['id'][]> = Object.fromEntries(
      Object.entries(featureByProduct).map(([productId, featureIds]) => [Number(productId), Array.from(featureIds)])
    );

    setForm(nextForm);
    setInitialForm(nextForm);
    setSelectedProducts(products);
    setInitialSelectedProducts(products);

    if (Object.keys(products).length > 0) {
      setActiveProductId(Number(Object.keys(products)[0]));
    } else {
      setActiveProductId(null);
    }

    setHasHydrated(true);
  }, [open, hasHydrated, isEdit, component, useCaseId]);

  const canManageRelations = isEdit;

  const saving = addingComponent || updatingComponent || componentLoading || updatingJoins;

  const normalizedSelectedProducts = useMemo(() => {
    return Object.fromEntries(
      Object.entries(selectedProducts)
        .map(([pid, ids]) => [pid, [...ids].sort((a, b) => a - b)])
        .sort(([a], [b]) => Number(a) - Number(b))
    );
  }, [selectedProducts]);

  const normalizedInitialSelectedProducts = useMemo(() => {
    return Object.fromEntries(
      Object.entries(initialSelectedProducts)
        .map(([pid, ids]) => [pid, [...ids].sort((a, b) => a - b)])
        .sort(([a], [b]) => Number(a) - Number(b))
    );
  }, [initialSelectedProducts]);

  const hasUnsavedChanges = useMemo(() => {
    return (
      form.name !== initialForm.name ||
      form.short_description !== initialForm.short_description ||
      form.description !== initialForm.description ||
      form.order !== initialForm.order ||
      JSON.stringify(normalizedSelectedProducts) !== JSON.stringify(normalizedInitialSelectedProducts)
    );
  }, [normalizedSelectedProducts, normalizedInitialSelectedProducts, form.name, initialForm.name, form.short_description, initialForm.short_description, form.description, initialForm.description, form.order, initialForm.order]);

  const resetAndClose = () => {
    setHasHydrated(false);
    setForm({
      name: '',
      description: '',
      short_description: "",
      order: 0,
      use_case: useCaseId,
    });
    setInitialForm({
      name: '',
      short_description: "",
      description: '',
      order: 0,
      use_case: useCaseId,
    });
    setSelectedProducts({});
    setInitialSelectedProducts({});
    setActiveProductId(null);
    setConfirmCloseOpen(false);
    onClose();
  };

  const onCloseDialog = () => {
    if (readOnly || !hasUnsavedChanges) {
      resetAndClose();
      return;
    }
    setConfirmCloseOpen(true);
  };

  const handleCancelClose = () => {
    setConfirmCloseOpen(false);
  };

  const handleConfirmClose = () => {
    resetAndClose();
  };

  const handleSave = async () => {
    if (readOnly) {
      onCloseDialog();
      return;
    }

    let finalId = null;
    if (mode === 'create') {
      const created: any = await addComponent({ ...form, use_case: useCaseId });
      finalId = created?.id;
    } else if (isEdit && componentId) {
      await updateComponent({ ...form, use_case: useCaseId, id: componentId });
      finalId = componentId;
    }

    if (finalId) {
      const selectedFeatures = new Set(Object.values(selectedProducts).flat());
      await updateJoins({ selectedItems: selectedFeatures, attachingToId: finalId });
    }

    onSaved();
    resetAndClose();
  };

  const handleSelectedFeaturesChange = (newIds: number[]) => {
    if (!activeProductId) return;

    setSelectedProducts((prev) => {
      const next = { ...prev, [activeProductId]: newIds };
      if (!newIds.length) delete next[activeProductId];
      return next;
    });
  };

  const handleProductChipClick = (pid: number | null) => {
    if (readOnly) return;
    setActiveProductId(pid);
  };

  const handleProductChipDelete = (pid: number) => {
    if (!readOnly) {
      setSelectedProducts((prev) => {
        const { [pid]: _, ...next } = prev;
        return next;
      });
      if (activeProductId === pid) {
        setActiveProductId(null);
      }
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onCloseDialog} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">
              {readOnly
                ? mode === 'create'
                  ? 'View Security Use Case Component'
                  : `View Component: ${form?.name || `#${componentId}`}`
                : mode === 'create'
                  ? 'Create Security Use Case Component'
                  : `Component: ${form?.name || `#${componentId}`}`}
            </Typography>

            {isEdit && (
              <SnackableCourseButton
                entityType="security_use_case_component"
                entityId={componentId}
                entityName={form.name}
                disabled={!isEdit}
              />
            )}
          </Box>
        </DialogTitle>

        <DialogContent>
          {saving && (
            <Box display="flex" alignItems="center" gap={2} py={1}>
              <CircularProgress size={18} />
              <Typography variant="body2">Loading…</Typography>
            </Box>
          )}

          <TextField
            fullWidth
            margin="normal"
            label="Component Name"
            value={form.name ?? ''}
            onChange={(e) => !readOnly && setForm((p: any) => ({ ...p, name: e.target.value }))}
            disabled={readOnly}
          />

          <TextField
            fullWidth
            margin="normal"
            label="Short Description"
            value={form.short_description ?? ""}
            onChange={(e) => !readOnly && setForm((p: any) => ({ ...p, short_description: e.target.value }))}
            multiline
            minRows={2}
            disabled={readOnly}
          />

          <TextField
            fullWidth
            margin="normal"
            label="Description"
            value={form.description ?? ''}
            onChange={(e) => !readOnly && setForm((p: any) => ({ ...p, description: e.target.value }))}
            multiline
            minRows={2}
            disabled={readOnly}
          />

          <TextField
            fullWidth
            margin="normal"
            label="Order"
            type="number"
            value={form.order ?? 0}
            onChange={(e) => !readOnly && setForm((p: any) => ({ ...p, order: Number(e.target.value) }))}
            disabled={readOnly}
          />

          <Box mt={2}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Products with selected features
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {selectedProductChips.length ? (
                selectedProductChips.map((p: any) => {
                  const pid = p.id;
                  const isSelected = activeProductId === pid;
                  const count = (selectedProducts[pid] || []).length;

                  return (
                    <Chip
                      key={pid}
                      label={`${p.name} (${count})`}
                      size="small"
                      color={isSelected ? 'primary' : 'default'}
                      variant={isSelected ? 'filled' : 'outlined'}
                      onClick={() => handleProductChipClick(pid)}
                      onDelete={readOnly ? undefined : () => handleProductChipDelete(pid)}
                    />
                  );
                })
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No products selected yet.
                </Typography>
              )}
            </Box>
          </Box>

          <Box mt={2}>
            <FormControl fullWidth disabled={productsLoading || readOnly}>
              <InputLabel id="uc-product-label">Cisco Product</InputLabel>
              <Select
                labelId="uc-product-label"
                label="Cisco Product"
                value={activeProductId || ''}
                onChange={(e) => {
                  if (readOnly) return;
                  const val = e.target.value && e.target.value !== '' ? Number(e.target.value) : null;
                  setActiveProductId(val);
                }}
              >
                <MenuItem value="">
                  <em>{productsLoading ? 'Loading products...' : 'Select a Cisco product'}</em>
                </MenuItem>
                {(ciscoProducts ?? []).map((p: any) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box mt={2}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Supporting Features {activeProductId ? `for ${ciscoProductById.get(activeProductId)?.name}` : '(select a product first)'}
            </Typography>

            <Autocomplete
              multiple
              disableCloseOnSelect
              disabled={!activeProductId || readOnly}
              options={features ?? []}
              value={activeSelectedFeatures}
              isOptionEqualToValue={(opt: any, val: any) => opt.id === val.id}
              getOptionLabel={(opt: any) => opt?.name ?? ''}
              onChange={(_, newValues) => {
                if (readOnly) return;
                const ids = newValues.map((v: any) => v.id);
                handleSelectedFeaturesChange(ids);
              }}
              renderOption={(props, option: any, { selected }) => (
                <li {...props} key={option.id}>
                  <Checkbox checked={selected} size="small" sx={{ mr: 1 }} />
                  <Tooltip title={option.description || ''} placement="right" arrow>
                    <Box sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{option.name}</Box>
                  </Tooltip>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={featuresLoading ? 'Loading features...' : 'Select Supporting Features'}
                  placeholder={
                    activeProductId
                      ? featuresLoading
                        ? 'Loading features...'
                        : 'Search features...'
                      : 'Select a product first'
                  }
                />
              )}
              loading={featuresLoading && !!activeProductId}
            />
          </Box>

          <Divider sx={{ my: 3 }} />

          {canManageRelations && (
            <Box mb={3}>
              <ResourceRelationPanel
                joinEndpoint="db/security-use-case-component-to-resource"
                joinCacheKey={CacheKey.GtmSolutionUseCaseComponentToResource}
                parentField="security_use_case_component"
                parentId={componentId ?? null}
                title="Resources"
                showNameColumn={false}
                maxHeight={250}
                invalidateKeys={[CacheKey.GtmSolutionUseCaseComponent]}
                disabled={!canManageRelations}
                readOnly={readOnly}
              />
            </Box>
          )}

          <TagManagementSection
            parentId={componentId ?? null}
            parentType={CacheKey.GtmSolutionUseCaseComponent}
            disabled={!canManageRelations || readOnly}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={onCloseDialog} disabled={saving}>
            {readOnly ? 'Close' : 'Cancel'}
          </Button>
          {!readOnly && (
            <Button onClick={handleSave} variant="contained" disabled={saving}>
              Save
            </Button>
          )}
        </DialogActions>

        {courseManagerOpen && (
          <SnackableCourseManager
            open={courseManagerOpen}
            entityType="security_use_case_component"
            entityId={componentId}
            onClose={() => setCourseManagerOpen(false)}
          />
        )}
      </Dialog>

      <AdminConfirmDialog
        open={confirmCloseOpen}
        title="Discard Changes"
        message={
          <Box>
            <Typography variant="body1" gutterBottom>
              You have unsaved changes in this security use case component editor.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              If you continue, your changes will be lost.
            </Typography>
          </Box>
        }
        confirmLabel="Discard Changes"
        cancelLabel="Continue Editing"
        confirmColor="warning"
        loading={saving}
        onConfirm={handleConfirmClose}
        onCancel={handleCancelClose}
      />
    </>
  );
}